﻿package br.com.htms.inspeccaocampo.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.CampoConfig
import br.com.htms.inspeccaocampo.form.AppController
import br.com.htms.inspeccaocampo.platform.randomUuid
import br.com.htms.inspeccaocampo.shared.ui.components.FormFieldDefaults
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/** Callbacks registrados pela tela de Config para a TopAppBar (Voltar / Salvar). */
class ConfigHeaderActions {
    var onSalvar: (() -> Unit)? = null
    var onVoltar: (() -> Unit)? = null
}

private enum class ConfigSubPage {
    HUB,
    INSPECAO,
    ALOJAMENTO,
    OCORRENCIAS
}

@Composable
fun SharedConfigScreen(
    controller: AppController,
    snackbar: SnackbarHostState,
    headerActions: ConfigHeaderActions,
    onVoltar: () -> Unit
) {
    val configAtual by controller.appConfig.collectAsState()
    val modoTreinamento by controller.modoTreinamento.collectAsState()
    val authUser by controller.authUser.collectAsState()
    val scope = rememberCoroutineScope()

    var draft by remember(configAtual) { mutableStateOf(configAtual) }
    var novoCampoTitulo by remember { mutableStateOf("") }
    var importJson by remember { mutableStateOf("") }
    var mostrarImport by remember { mutableStateOf(false) }
    var subPage by remember { mutableStateOf(ConfigSubPage.HUB) }

    fun salvarAplicar() {
        if (controller.aplicarConfiguracao(draft)) {
            scope.launch { snackbar.showSnackbar("Configuração salva e aplicada.") }
        } else {
            scope.launch {
                snackbar.showSnackbar(
                        controller.mensagem.value?.ifBlank { "Não foi possível salvar." }
                            ?: "Não foi possível salvar."
                    )
            }
        }
    }

    SideEffect {
        headerActions.onSalvar = { salvarAplicar() }
        headerActions.onVoltar = {
            if (subPage != ConfigSubPage.HUB) {
                subPage = ConfigSubPage.HUB
            } else {
                onVoltar()
            }
        }
    }
    DisposableEffect(headerActions) {
        onDispose {
            headerActions.onSalvar = null
            headerActions.onVoltar = null
        }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        when (subPage) {
            ConfigSubPage.HUB -> {
                Text(
                    "Configuração",
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    "Alterações são aplicadas no app sem gerar novo build. Use Salvar/Aplicar no topo para confirmar.",
                    style = MaterialTheme.typography.bodySmall
                )

                if (authUser?.isAdmin == true) {
                    SharedAdminUsersPanel(
                        onMessage = { msg -> scope.launch { snackbar.showSnackbar(msg) } }
                    )
                    SharedAdminObrasPanel(
                        controller = controller,
                        onMessage = { msg -> scope.launch { snackbar.showSnackbar(msg) } }
                    )
                }

                Card(colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)) {
                    Column(
                        modifier = Modifier.padding(12.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        Text("Identidade", fontWeight = FontWeight.Bold)
                        OutlinedTextField(
                            value = draft.nomeApp,
                            onValueChange = { draft = draft.copy(nomeApp = it) },
                            label = { Text("Nome no topo") },
                            modifier = Modifier.fillMaxWidth(),
                            colors = FormFieldDefaults.textFieldColors()
                        )
                        OutlinedTextField(
                            value = draft.nomeInspecao,
                            onValueChange = { draft = draft.copy(nomeInspecao = it) },
                            label = { Text("Subtítulo do portal (ex.: Portal QSSMA)") },
                            modifier = Modifier.fillMaxWidth(),
                            colors = FormFieldDefaults.textFieldColors()
                        )
                        Text(
                            if (draft.bannerPath != null) "Banner personalizado ativo"
                            else "Usando banner padrão (nome no topo)",
                            style = MaterialTheme.typography.bodySmall
                        )
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            OutlinedButton(
                                onClick = {
                                    controller.definirBannerDaGaleria()
                                    scope.launch {
                                        delay(400)
                                        draft = controller.appConfig.value
                                    }
                                },
                                modifier = Modifier.weight(1f)
                            ) { Text("Trocar logo/banner") }
                            OutlinedButton(
                                onClick = {
                                    controller.restaurarBannerPadrao()
                                    draft = draft.copy(bannerPath = null)
                                    scope.launch { snackbar.showSnackbar("Banner padrão restaurado.") }
                                },
                                modifier = Modifier.weight(1f)
                            ) { Text("Restaurar padrão") }
                        }
                    }
                }

                Card(colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)) {
                    Column(
                        modifier = Modifier.padding(12.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        Text("Configuração por checklist", fontWeight = FontWeight.Bold)
                        Text(
                            "Abra a configuração de campos de cada formulário.",
                            style = MaterialTheme.typography.bodySmall
                        )
                        ChecklistConfigEntry(
                            titulo = "Inspeção de Campo Diária",
                            descricao = "Campos de verificação (Sim/Não/N/A), obrigatoriedade e fotos."
                        ) { subPage = ConfigSubPage.INSPECAO }
                        ChecklistConfigEntry(
                            titulo = "Relatório Alojamento",
                            descricao = "Parte 1 (fotos) e Parte 2 (checklist) em uma só tela."
                        ) { subPage = ConfigSubPage.ALOJAMENTO }
                        ChecklistConfigEntry(
                            titulo = "Comunicação de ocorrências",
                            descricao = "Ativar/desativar e renomear blocos do formulário."
                        ) { subPage = ConfigSubPage.OCORRENCIAS }
                    }
                }

                Card(colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)) {
                    Column(
                        modifier = Modifier.padding(12.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        Text("Modo treinamento", fontWeight = FontWeight.Bold)
                        Text(
                            if (modoTreinamento) "Ativo — inspeções de exemplo não vão ao histórico."
                            else "Inativo."
                        )
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            OutlinedButton(
                                onClick = { controller.ativarTreinamento() },
                                modifier = Modifier.weight(1f),
                                enabled = !modoTreinamento
                            ) { Text("Ativar") }
                            OutlinedButton(
                                onClick = { controller.desativarTreinamento() },
                                modifier = Modifier.weight(1f),
                                enabled = modoTreinamento
                            ) { Text("Desativar") }
                        }
                    }
                }

                Card(colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)) {
                    Column(
                        modifier = Modifier.padding(12.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        Text("Backup da configuração", fontWeight = FontWeight.Bold)
                        OutlinedButton(
                            onClick = { controller.exportarConfig() },
                            modifier = Modifier.fillMaxWidth()
                        ) { Text("Exportar configuração (JSON)") }
                        OutlinedButton(
                            onClick = { mostrarImport = !mostrarImport },
                            modifier = Modifier.fillMaxWidth()
                        ) { Text(if (mostrarImport) "Ocultar importação" else "Importar configuração (colar JSON)") }
                        if (mostrarImport) {
                            OutlinedTextField(
                                value = importJson,
                                onValueChange = { importJson = it },
                                label = { Text("Cole o JSON aqui") },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 4,
                                colors = FormFieldDefaults.textFieldColors()
                            )
                            Button(
                                onClick = {
                                    controller.importarConfigTexto(importJson)
                                    draft = controller.appConfig.value
                                    importJson = ""
                                    mostrarImport = false
                                },
                                modifier = Modifier.fillMaxWidth(),
                                enabled = importJson.isNotBlank()
                            ) { Text("Aplicar JSON importado") }
                        }
                    }
                }
            }

            ConfigSubPage.INSPECAO -> {
                ConfigSubHeader(
                    titulo = "Inspeção de Campo Diária",
                    onVoltarHub = { subPage = ConfigSubPage.HUB }
                )
                CamposVerificacaoEditor(
                    campos = draft.campos,
                    novoCampoTitulo = novoCampoTitulo,
                    onNovoCampoTitulo = { novoCampoTitulo = it },
                    onCamposChange = { draft = draft.copy(campos = it) },
                    permitirAdicionar = true,
                    mostrarFotos = true
                )
            }

            ConfigSubPage.ALOJAMENTO -> {
                ConfigSubHeader(
                    titulo = "Relatório Alojamento",
                    onVoltarHub = { subPage = ConfigSubPage.HUB }
                )
                Text(
                    "Parte 1 — Relatório fotográfico",
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.titleMedium
                )
                Text(
                    "Ative/desative itens e ajuste títulos. Itens inativos somem de novos formulários.",
                    style = MaterialTheme.typography.bodySmall
                )
                CamposVerificacaoEditor(
                    campos = draft.camposAlojamentoFoto,
                    novoCampoTitulo = "",
                    onNovoCampoTitulo = {},
                    onCamposChange = { draft = draft.copy(camposAlojamentoFoto = it) },
                    permitirAdicionar = false,
                    mostrarFotos = true,
                    rotuloFotoSim = "Foto obrigatória"
                )
                Text(
                    "Parte 2 — Checklist",
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.titleMedium
                )
                CamposVerificacaoEditor(
                    campos = draft.camposAlojamentoChecklist,
                    novoCampoTitulo = "",
                    onNovoCampoTitulo = {},
                    onCamposChange = { draft = draft.copy(camposAlojamentoChecklist = it) },
                    permitirAdicionar = false,
                    mostrarFotos = false
                )
            }

            ConfigSubPage.OCORRENCIAS -> {
                ConfigSubHeader(
                    titulo = "Comunicação de ocorrências",
                    onVoltarHub = { subPage = ConfigSubPage.HUB }
                )
                Text(
                    "Ative/desative blocos do formulário e ajuste os rótulos exibidos.",
                    style = MaterialTheme.typography.bodySmall
                )
                CamposVerificacaoEditor(
                    campos = draft.camposOcorrencias,
                    novoCampoTitulo = "",
                    onNovoCampoTitulo = {},
                    onCamposChange = { draft = draft.copy(camposOcorrencias = it) },
                    permitirAdicionar = false,
                    mostrarFotos = false,
                    mostrarObrigatorio = false
                )
            }
        }
    }
}

@Composable
private fun ConfigSubHeader(titulo: String, onVoltarHub: () -> Unit) {
    Text(titulo, style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Bold)
    TextButton(onClick = onVoltarHub) { Text("← Voltar ao menu Config") }
}

@Composable
private fun ChecklistConfigEntry(
    titulo: String,
    descricao: String,
    onOpen: () -> Unit
) {
    Card(
        onClick = onOpen,
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(titulo, fontWeight = FontWeight.Bold)
            Text(descricao, style = MaterialTheme.typography.bodySmall)
            Text("Abrir configuração →", color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.SemiBold)
        }
    }
}

@Composable
private fun CamposVerificacaoEditor(
    campos: List<CampoConfig>,
    novoCampoTitulo: String,
    onNovoCampoTitulo: (String) -> Unit,
    onCamposChange: (List<CampoConfig>) -> Unit,
    permitirAdicionar: Boolean,
    mostrarFotos: Boolean,
    mostrarObrigatorio: Boolean = true,
    rotuloFotoSim: String = "Foto se Sim",
    rotuloFotoNao: String = "Foto se Não"
) {
    Card(colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Text("Campos", fontWeight = FontWeight.Bold)
            campos.forEachIndexed { index, campo ->
                CampoConfigEditor(
                    campo = campo,
                    onChange = { atualizado ->
                        onCamposChange(
                            campos.toMutableList().also { it[index] = atualizado }
                        )
                    },
                    onRemover = if (permitirAdicionar) {
                        { onCamposChange(campos.filterIndexed { i, _ -> i != index }) }
                    } else null,
                    mostrarFotos = mostrarFotos,
                    mostrarObrigatorio = mostrarObrigatorio,
                    rotuloFotoSim = rotuloFotoSim,
                    rotuloFotoNao = rotuloFotoNao
                )
            }
            if (permitirAdicionar) {
                OutlinedTextField(
                    value = novoCampoTitulo,
                    onValueChange = onNovoCampoTitulo,
                    label = { Text("Novo campo") },
                    modifier = Modifier.fillMaxWidth(),
                    colors = FormFieldDefaults.textFieldColors()
                )
                OutlinedButton(
                    onClick = {
                        val titulo = novoCampoTitulo.trim()
                        if (titulo.isNotEmpty()) {
                            onCamposChange(
                                campos + CampoConfig(
                                    id = "campo_${randomUuid()}",
                                    titulo = titulo
                                )
                            )
                            onNovoCampoTitulo("")
                        }
                    },
                    modifier = Modifier.fillMaxWidth()
                ) { Text("Adicionar campo") }
            }
        }
    }
}

@Composable
private fun CampoConfigEditor(
    campo: CampoConfig,
    onChange: (CampoConfig) -> Unit,
    onRemover: (() -> Unit)?,
    mostrarFotos: Boolean,
    mostrarObrigatorio: Boolean,
    rotuloFotoSim: String,
    rotuloFotoNao: String
) {
    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier.padding(10.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            OutlinedTextField(
                value = campo.titulo,
                onValueChange = { onChange(campo.copy(titulo = it)) },
                label = { Text("Título") },
                modifier = Modifier.fillMaxWidth(),
                colors = FormFieldDefaults.textFieldColors()
            )
            SwitchRow("Ativo", campo.ativo) { onChange(campo.copy(ativo = it)) }
            if (mostrarObrigatorio) {
                SwitchRow("Obrigatório", campo.obrigatorio) { onChange(campo.copy(obrigatorio = it)) }
            }
            if (mostrarFotos) {
                SwitchRow(rotuloFotoSim, campo.fotoObrigatoriaSeSim) {
                    onChange(campo.copy(fotoObrigatoriaSeSim = it))
                }
                if (rotuloFotoNao.isNotBlank() && rotuloFotoSim != "Foto obrigatória") {
                    SwitchRow(rotuloFotoNao, campo.fotoObrigatoriaSeNao) {
                        onChange(campo.copy(fotoObrigatoriaSeNao = it))
                    }
                }
            }
            if (onRemover != null) {
                TextButton(onClick = onRemover) { Text("Remover campo") }
            }
        }
    }
}

@Composable
private fun SwitchRow(label: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(label)
        Switch(checked = checked, onCheckedChange = onChange)
    }
}
